import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useState, useEffect, useRef } from 'react';
import { MdCalendarToday } from 'react-icons/md';

interface CandidatesDateFieldProps {
  fieldId: number;
  value?: { min: string | null; max: string | null };
  onDateChange: (fieldId: number, min: string | null, max: string | null) => void;
}

export default function CandidatesDateField({
  fieldId,
  value,
  onDateChange,
}: CandidatesDateFieldProps) {
  // valueから初期値を設定
  const getInitialDateRange = (): [Date | null, Date | null] => {
    if (value && (value.min || value.max)) {
      return [value.min ? new Date(value.min) : null, value.max ? new Date(value.max) : null];
    }
    return [null, null];
  };

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getInitialDateRange());
  const [startDate, endDate] = dateRange;
  const previousFormattedRef = useRef<{ min: string | null; max: string | null }>({
    min: null,
    max: null,
  });
  const onDateChangeRef = useRef(onDateChange);

  const formatDate = (date: Date | null): string | null => {
    if (!date) return null;
    return date.toISOString().split('T')[0];
  };

  // onDateChangeRefを最新に保つ
  useEffect(() => {
    onDateChangeRef.current = onDateChange;
  }, [onDateChange]);

  // valueが変更されたときに日付範囲を更新（外部からの値変更のみ）
  useEffect(() => {
    if (value) {
      const newRange: [Date | null, Date | null] = [
        value.min ? new Date(value.min) : null,
        value.max ? new Date(value.max) : null,
      ];
      // 現在の値と異なる場合のみ更新
      const [currentStart, currentEnd] = dateRange;
      const isSameStart =
        (currentStart && newRange[0] && currentStart.getTime() === newRange[0].getTime()) ||
        (!currentStart && !newRange[0]);
      const isSameEnd =
        (currentEnd && newRange[1] && currentEnd.getTime() === newRange[1].getTime()) ||
        (!currentEnd && !newRange[1]);

      if (!isSameStart || !isSameEnd) {
        setDateRange(newRange);
      }
    }
  }, [value]);

  // 日付が変更されたときのみ親コンポーネントに通知（前回値と比較）
  useEffect(() => {
    const currentFormatted = {
      min: formatDate(startDate),
      max: formatDate(endDate),
    };

    // 前回の値と比較して変更があった場合のみ呼び出し
    if (
      currentFormatted.min !== previousFormattedRef.current.min ||
      currentFormatted.max !== previousFormattedRef.current.max
    ) {
      onDateChangeRef.current(fieldId, currentFormatted.min, currentFormatted.max);
      previousFormattedRef.current = currentFormatted;
    }
  }, [fieldId, startDate, endDate]);

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0].replace(/-/g, '/');
  };

  const displayText =
    startDate || endDate
      ? `${formatDisplayDate(startDate)} ~ ${formatDisplayDate(endDate)}`
      : '日付を選択';

  return (
    <div className="relative">
      <DatePicker
        locale={'ja'}
        selectsRange
        calendarClassName="calendar-candidates-date-field"
        startDate={startDate as Date | undefined}
        endDate={endDate as Date | undefined}
        onChange={(update: [Date | null, Date | null]) => {
          setDateRange(update);
        }}
        placeholderText="日付を選択"
        dateFormat="yyyy/MM/dd"
        customInput={
          <div className="flex h-[42px] w-[300px] items-center rounded border border-solid border-border px-2">
            <span className="">{displayText}</span>
            <div className="ml-auto">
              <MdCalendarToday className="size-6" />
            </div>
          </div>
        }
      />
    </div>
  );
}
